import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CheckboxComponent } from '../../@core/components/ui/checkbox';
import { DataViewModule } from 'primeng/dataview';
import { DatepickerComponent } from '../../@core/components/ui/datepicker/datepicker.component';
import { FileUploadModule } from 'primeng/fileupload';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ListboxModule } from 'primeng/listbox';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { PickListModule } from 'primeng/picklist';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SliderModule } from 'primeng/slider';
import { TabsModule } from 'primeng/tabs';
import { ToggleComponent } from '../../@core/components/ui/toggle/toggle.component';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonComponent } from '../../@core/components/ui/button/button.component';
import { USER_TYPE } from '../../@core/models/enums';
import { IFile, ILinkDocument, IMilestone, IShareUser, IUser, LINKED_DOCUMENT_TYPE, MILESTONE_TYPE, SHARE_ACCESS, SHARE_FEATURE } from '../../@core/models/model';
import { AuthenticationService } from '../../@core/services/authentication.service';
import { ToastService } from '../../@core/services/toast.service';
import { FileDropComponent } from '../../@shared/components/file-drop/file-drop.component';
import { FileDropTypes } from '../../@shared/components/file-drop/file-drop.types';
import { ShareToUsersComponent } from '../../@shared/components/share-to-users/share-to-users.component';
import { UploadDocsComponent } from '../../@shared/components/upload-docs/upload-docs.component';
import { SanitizeHtmlPipe } from '../../@shared/pipes/sanitizeHtml.pipe';
import { MilestonesComponent } from '../../contract-management/components/milestones/milestones.component';
import { KNOWLEDGE_BASE_SVG } from '../../contract-management/components/settings/knowledgebase/knowledge-base-svg';
import { CUSTOMISATION_TONE, IContract, IDocumentForm, KNOWLEDGE_SCOPE, VERBOSITY } from '../../contract-management/models/contract-management.model';
import { IDocumentType } from '../../contract-management/models/settings.model';
import { ContractService } from '../../contract-management/services/contracts.service';
import { KnowledgeMappingService } from '../../contract-management/services/knowledge-mapping.service';
import { IKnowledgeBaseStore, KnowledgeBaseService } from '../../contract-management/services/knowledgebase.service';
import { MileStoneService } from '../../contract-management/services/milestone.service';
import { RepositoryService } from '../../contract-management/services/repository.service';
import { ShareContractService } from '../../contract-management/services/share-contract.service';
import { UserCreditsStateService } from '../../features/credits/services/user-credits-state.service';
import { SearchBarComponent } from '../../@shared/components/search-bar/search-bar.component';
import { ExpandableTextareaComponent } from '../../@shared/components/expandable-textarea/expandable-textarea.component';
import { TextCasePipe } from '../../@shared/pipes/textCase.pipe';
import { CaseType } from 'src/app/@shared/services/case-format.service';
import { DatePickerConfig } from 'src/app/@core/components/ui/datepicker';
import { DialogComponent } from '../../@core/components/ui/dialog/dialog.component';
import { StepperComponent } from '../../@shared/components/stepper';
import { StepperStepDirective } from '../../@shared/components/stepper-layout/stepper-step.directive';
import { StepperConfig, StepperStepConfig } from '../../@shared/components/stepper-layout/stepper.types';

@Component({
  selector: 'app-create-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FileUploadModule,
    StepperComponent,
    StepperStepDirective,
    FileDropComponent,
    PickListModule,
    PaginatorModule,
    SearchBarComponent,
    ShareToUsersComponent,
    ProgressSpinnerModule,
    ToggleComponent,
    TabsModule,
    DataViewModule,
    SanitizeHtmlPipe,
    ListboxModule,
    CheckboxComponent,
    InputTextModule,
    TooltipModule,
    InputGroupModule,
    InputGroupAddonModule,
    DatepickerComponent,
    SliderModule,
    RadioButtonModule,
    MilestonesComponent,
    ButtonComponent,
    SearchBarComponent,
    ExpandableTextareaComponent,
    TextCasePipe,
    DialogComponent,
    UploadDocsComponent,
  ],
  templateUrl: './create-form.component.html',
  styleUrl: './create-form.component.scss'
})

export class CreateFormComponent implements OnInit, OnDestroy {
  Fetching = false;
  Saving = false;

  CASE_TYPE = CaseType;

  @Input() docType: IDocumentType | null = null;
  @Input() data: IDocumentForm | null = null;
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() project: string = '';
  @Input() contract: IContract | null = null;
  @Input() contractDueDate: string | Date | undefined = '';
  @Input() projectDueDate: Date | undefined = undefined;
  @Input() scope: KNOWLEDGE_SCOPE = KNOWLEDGE_SCOPE.WORKSPACE;
  @HostBinding('style.display') get hostDisplay() { return this.visible ? '' : 'none'; }
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() closeEditForm = new EventEmitter<void>();

  // Stepper configs
  readonly uploadFormStepConfig: StepperStepConfig = {
    id: 'upload-form',
    label: 'Upload Form',
    title: 'Upload Form',
    subtitle: 'Upload the form document to get started',
    isValid: () => this.formFiles.length > 0,
    invalidMessage: 'Please upload a form file before proceeding',
  };

  readonly knowledgeBaseStepConfig: StepperStepConfig = {
    id: 'knowledge-base',
    label: 'Add Knowledge Base',
    title: 'Add Knowledge Base',
    subtitle: 'Select knowledge bases and files for AI reference',
    isValid: () => this.useQnaPair || this.knowledgeBaseStore.selectedStores.length > 0 || this.knowledgeBaseFiles.selectedFiles.length > 0,
    invalidMessage: 'Please select at least one knowledge base or file, or enable Q&A Bank',
  };

  readonly customInstructionsStepConfig: StepperStepConfig = {
    id: 'custom-instructions',
    label: 'Custom Instructions',
    title: 'Custom Instructions',
    subtitle: 'Set tone, verbosity and custom prompt instructions',
  };

  readonly milestonesStepConfig: StepperStepConfig = {
    id: 'milestones',
    label: 'Add Milestones',
    title: 'Add Milestones',
    subtitle: 'Set key milestones for this form',
    skipable: true,
  };

  readonly teamStepConfig: StepperStepConfig = {
    id: 'team',
    label: 'Add Team',
    title: 'Add Team',
    subtitle: 'Invite users to collaborate on this form',
  };

  private _stepperConfig: StepperConfig | null = null;

  get stepperConfig(): StepperConfig {
    if (!this._stepperConfig) {
      this._stepperConfig = this.buildStepperConfig();
    }
    return this._stepperConfig;
  }

  private buildStepperConfig(): StepperConfig {
    if (this.mode === 'create') {
      return {
        steps: [
          this.uploadFormStepConfig,
          this.knowledgeBaseStepConfig,
          this.customInstructionsStepConfig,
          this.milestonesStepConfig,
          this.teamStepConfig,
        ],
        currentStepIndex: 0,
        dialogWidth: '75vw',
      };
    }
    // Edit mode: step 0 (upload) is skipped by starting at index 1
    return {
      steps: [
        { id: 'upload-form', label: 'Upload Form', skipable: true },
        { id: 'knowledge-base', label: 'Edit Knowledge Base', title: 'Edit Knowledge Base', subtitle: 'Update knowledge bases and files for AI reference', isValid: this.knowledgeBaseStepConfig.isValid, invalidMessage: this.knowledgeBaseStepConfig.invalidMessage },
        { id: 'custom-instructions', label: 'Edit Custom Instructions', title: 'Edit Custom Instructions', subtitle: 'Update tone, verbosity and custom prompt instructions' },
        { id: 'milestones', label: 'Edit Milestones', title: 'Edit Milestones', subtitle: 'Update key milestones for this form', skipable: true },
        { id: 'team', label: 'Edit Team', title: 'Edit Team', subtitle: 'Manage team members for this form' },
      ],
      currentStepIndex: 1,
      dialogWidth: '75vw',
    };
  }

  // File uploads
  formFiles: File[] = [];
  knowledgeBaseFile: File | null = null;
  minimumDate: Date = new Date();

  // Form fields
  useQnaPair = true;
  customInstructions = '';

  // Tab management
  activeTabIndex = '0';

  // Knowledge Base selection management
  knowledgeBaseSearchTerm: string = '';
  selectedKnowledgeBases: IKnowledgeBaseStore[] = [];
  filteredKnowledgeBases: IKnowledgeBaseStore[] = [];

  // Document
  document: IDocumentForm | null = null;
  linkDocs: ILinkDocument[] = [];
  searchedTermInAvailable: string = ""
  searchTermInSelected: string = ""
  SVGS = KNOWLEDGE_BASE_SVG;
  knowledgeBaseStore: {
    skip: number,
    limit: number,
    searchValue: string,
    selectAll: boolean,
    stores: (IKnowledgeBaseStore & { selected: boolean })[],
    currentPageStores: (IKnowledgeBaseStore & { selected: boolean })[],

    totalRecords: number,
    loading: boolean,
    selectedStores: IKnowledgeBaseStore[],
  } = {
      skip: 0,
      limit: 10,
      searchValue: '',
      selectAll: false,
      stores: [],
      currentPageStores: [],
      totalRecords: 0,
      loading: false,
      selectedStores: []
    }

  // Knowledge base
  knowledgeBaseFiles: {
    skip: number,
    limit: number,
    searchValue: string,
    selectAll: boolean,
    files: (IFile & { selected: boolean })[],
    currentPageFiles: (IFile & { selected: boolean })[],
    totalRecords: number,
    loading: boolean,
    selectedFiles: IFile[],
  } = {
      skip: 0,
      limit: 10,
      searchValue: '',
      selectAll: false,
      files: [],
      currentPageFiles: [],
      totalRecords: 0,
      loading: false,
      selectedFiles: []
    }

  dueDate: Date = new Date()
  CustomisationTone = CUSTOMISATION_TONE
  selectedTone: CUSTOMISATION_TONE = CUSTOMISATION_TONE.NEUTRAL
  verbosityLevel: number = 0
  verbosity: VERBOSITY = VERBOSITY.CONCISE;
  shareDetails: { users: IShareUser[], anyone: SHARE_ACCESS, workspaceShare: boolean, projectShare: boolean } = { users: [], anyone: SHARE_ACCESS.RESTRICTED, workspaceShare: false, projectShare: false }
  workspaceShare: any = null
  projectShare: any = null
  currentUser: IUser | null | undefined = null
  usersToAddToShare: Partial<IShareUser>[] = []
  // Milestones
  milestones: IMilestone[] = []
  editableMilestones: Array<Partial<IMilestone>> = []
  deletedMilestoneIds: string[] = []

  rowsPerPageOptions = [10, 20, 50];


  public allowedFileTypes: string[] = [FileDropTypes.PDF, FileDropTypes.DOCX, FileDropTypes.DOC, FileDropTypes.XLSX, FileDropTypes.XLS];

  projectDueDateConfig: DatePickerConfig = {
    dateFormat: 'dd M yy',
    minDate: this.minimumDate,
    keepInvalid: true,
    showIcon: true,
    focusTrap: false,
  }

  constructor(
    private contractService: ContractService,
    private toastService: ToastService,
    private repositoryService: RepositoryService,
    private knowledgeBaseService: KnowledgeBaseService,
    private router: Router,
    private knowledgeMappingService: KnowledgeMappingService,
    private shareService: ShareContractService,
    private authService: AuthenticationService,
    private milestoneService: MileStoneService,
    private creditsState: UserCreditsStateService
  ) {
    this.authService.currentUser.subscribe(res => {
      this.currentUser = res
    });
  }
  ngOnInit(): void {
    // this.initializeOnOpen();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && changes['data'].currentValue) {
      this.initializeDataForEditMode();
    }
    if (changes['mode'] && !changes['mode'].firstChange) {
      this._stepperConfig = null; // invalidate cached config when mode changes
      // stepper resets via config change
    }
    if (changes['visible'] && changes['visible'].currentValue === true && !changes['visible'].firstChange) {
      this.initializeOnOpen();
    }
  }

  private resetFormState(): void {
    this.formFiles = [];
    this.knowledgeBaseFile = null;
    this.useQnaPair = true;
    this.customInstructions = '';
    this.activeTabIndex = '0';
    this.knowledgeBaseSearchTerm = '';
    this.selectedKnowledgeBases = [];
    this.filteredKnowledgeBases = [];
    this.document = null;
    this.linkDocs = [];
    this.knowledgeBaseStore = { skip: 0, limit: 10, searchValue: '', selectAll: false, stores: [], currentPageStores: [], totalRecords: 0, loading: false, selectedStores: [] };
    this.knowledgeBaseFiles = { skip: 0, limit: 10, searchValue: '', selectAll: false, files: [], currentPageFiles: [], totalRecords: 0, loading: false, selectedFiles: [] };
    this.dueDate = new Date();
    this.selectedTone = CUSTOMISATION_TONE.NEUTRAL;
    this.verbosityLevel = 0;
    this.verbosity = VERBOSITY.CONCISE;
    this.shareDetails = { users: [], anyone: SHARE_ACCESS.RESTRICTED, workspaceShare: false, projectShare: false };
    this.workspaceShare = null;
    this.projectShare = null;
    this.usersToAddToShare = [];
    this.milestones = [];
    this.editableMilestones = [];
    this.deletedMilestoneIds = [];
    this._stepperConfig = null;
  }

  private initializeOnOpen(): void {
    this.projectDueDateConfig.maxDate = this.projectDueDate;
    if (this.mode === 'edit') {
      this.initializeDataForEditMode();
    }
    else {
      this.resetFormState();
      this.shareDetails.users.push({
        name: this.currentUser?.fullName as string,
        user: this.currentUser as IUser,
        email: this.currentUser?.email as string,
        type: USER_TYPE.INTERNAL,
        role: SHARE_ACCESS.OWNER,
      })
      this.getDocTypeMilestones();
      this.getLinkedKnowledgeBaseMappings();
    }
  }


  private initializeDataForEditMode(): void {
    if (this.mode === 'edit' && this.data) {
      this.document = this.data as any;
      this.customInstructions = this.data.instructions || '';
      this.useQnaPair = this.data.useQnaPair ?? true;
      this.selectedTone = this.data.tone ?? CUSTOMISATION_TONE.NEUTRAL;
      if (this.data.verbosity) {
        this.verbosity = this.data.verbosity;
        switch (this.data.verbosity) {
          case VERBOSITY.CONCISE:
            this.verbosityLevel = 0;
            break;
          case VERBOSITY.MODERATE:
            this.verbosityLevel = 1;
            break;
          case VERBOSITY.DETAILED:
            this.verbosityLevel = 2;
            break;
        }
      }
      if (this.data.knowledgeBase && Array.isArray(this.data.knowledgeBase) && this.data.knowledgeBase.length > 0 && typeof this.data.knowledgeBase[0] === 'object') {
        this.knowledgeBaseStore.selectedStores = [...this.data.knowledgeBase as IKnowledgeBaseStore[]];
      }
      if (this.data.files && Array.isArray(this.data.files) && this.data.files.length > 0 && typeof this.data.files[0] === 'object') {
        this.knowledgeBaseFiles.selectedFiles = [...this.data.files as IFile[]];
      }
      if (this.contractDueDate) {
        this.dueDate = new Date(this.contractDueDate)
      }
      this.fetchFilesForKnowledgeBase(true, this.data.files as IFile[]);
      this.fetchStoresForKnowledgeBase(true, this.data.knowledgeBase as IKnowledgeBaseStore[]);
      this.shareService.getShareDetails((this.data.contract as IContract)?._id as string, SHARE_FEATURE.CONTRACT).subscribe(res => {
        if (res.success) {
          this.shareDetails = res.data
          this.workspaceShare = res.data.workspaceShare ?? null
          this.projectShare = res.data.projectShare ?? null
          this.shareDetails.workspaceShare = res.data.workspaceShare ? true : false
          this.shareDetails.projectShare = res.data.projectShare ? true : false

        }
      })
      this.getMilestones();
    }
  }

  ngOnDestroy(): void {
    this.knowledgeBaseStore.stores = [];
    this.knowledgeBaseStore.currentPageStores = [];
    this.knowledgeBaseFiles.files = [];
    this.knowledgeBaseFiles.currentPageFiles = [];
  }


  /**
   * Handle form file upload event
   * @param event - File upload event containing files
   */
  onFormUpload(event: File | File[]) {
    event = event as File[]
    this.formFiles = event.length > 0 ? event : [];
  }

  /**
   * Handle file removal from form document upload
   * @param file - File to be removed
   */
  onFormFileRemove(file: File) {
    this.formFiles = this.formFiles.filter(f => f !== file);
  }

  currentStepIndex = 0;

  onNextStep() {
    // Step 2 (custom instructions): sync verbosityLevel slider → verbosity enum before advancing
    if (this.currentStepIndex === 2) {
      this.verbosity = this.getVerbosityFromLevel(this.verbosityLevel);
    }
  }

  /**
   * Navigate to next step
   */
  onFinish() {
    // Both branches kick off agent work server-side: create → question extraction,
    // edit → re-answering when the generation settings changed.
    if (this.creditsState.agentExhausted()) {
      this.toastService.error("You've run out of credits. Request more to continue.");
      return;
    }
    const agentRemaining = this.creditsState.agentRemaining();
    if (agentRemaining > 0 && agentRemaining < 3) {
      this.toastService.warn(`Low credits (${agentRemaining} remaining). This task may not complete.`);
    }
    if (this.mode === 'create') {
      if (this.formFiles.length > 0) {
        this.Saving = true;
        this.contractService.createFormContract({
          linkedDocType: this.docType?._id ?? '',
          file: this.formFiles[0], // Keep the first file for backward compatibility
          project: this.project,
          files: Array.from(new Set(this.knowledgeBaseFiles.selectedFiles.map(file => file._id))),
          knowledgeBase: Array.from(new Set(this.knowledgeBaseStore.selectedStores.map(store => store._id))),
          useQnaPair: this.useQnaPair,
          instructions: this.customInstructions,
          tone: this.selectedTone,
          verbosity: this.verbosity,
          dueDate: this.dueDate,
          shareUsers: this.usersToAddToShare,
          milestones: this.milestones

        }).subscribe((res) => {
          if (res.success) {
            this.router.navigate(['edit-document'], {
              queryParams: {
                'documentId': res.data._id,
                'projectId': this.project
              }
            });
          }
          this.Saving = false;
        })
      }
    }
    else {
      this.contractService.updateFormContract({
        _id: this.document?._id ?? '',
        files: Array.from(new Set(this.knowledgeBaseFiles.selectedFiles.map(file => file._id))),
        knowledgeBase: Array.from(new Set(this.knowledgeBaseStore.selectedStores.map(store => store._id))),
        useQnaPair: this.useQnaPair,
        instructions: this.customInstructions,
        tone: this.selectedTone,
        verbosity: this.verbosity,
        dueDate: this.dueDate
      }).subscribe((res) => {
        if (res.success) {
          this.onMilestoneSave((this.document?.contract as IContract)?._id ?? '', { edited: this.editableMilestones, deleted: this.deletedMilestoneIds });
          this.closeEditForm.emit()
          this.toastService.success('Form updated successfully')
        }
      })

      this.saveShareDetails()

    }


  }

  changeSharedWithWorkspace(workspaceShare: boolean) {
    this.shareDetails.workspaceShare = workspaceShare
    const workspaceId = this.currentUser?.currentWorkspace as string
    this.workspaceShare = workspaceShare ? workspaceId : null
  }


  changeSharedWithProject(projectShare: boolean) {
    this.shareDetails.projectShare = projectShare
    this.projectShare = projectShare ? this.project : null
  }

  saveShareDetails() {
    if (!this.document?._id) return;
    const data = this.shareDetails
    data['workspaceShare'] = this.workspaceShare
    data['projectShare'] = this.projectShare
    this.shareService.updateShare(this.contract?._id as string, data, SHARE_FEATURE.CONTRACT).subscribe(res => {
      if (res.success) {
        this.toastService.success('Share details updated successfully')
      }
    })
  }

  getLinkedKnowledgeBaseMappings() {
    if (!this.docType) return;
    this.knowledgeMappingService.getKnowledgeMappingsByDocumentType({
      documentTypeId: this.docType?._id as string,
      scope: this.scope,
      ...(this.scope == KNOWLEDGE_SCOPE.PROJECT ? { project: this.project as string } : {})
    }).subscribe(res => {
      if (res && res.success) {
        const selectedStores: IKnowledgeBaseStore[] = []
        const selectedFiles: IFile[] = []
        if (res.data?.length) {
          for (const item of res.data) {
            if (item.linkedDocumentType == LINKED_DOCUMENT_TYPE.KNOWLEDGE_BASE_STORE) {
              selectedStores.push(item.linkedDocument as IKnowledgeBaseStore)
            } else if (item.linkedDocumentType == LINKED_DOCUMENT_TYPE.FILE) {
              selectedFiles.push(item.linkedDocument as IFile)
            }
          }
        }
        this.fetchStoresForKnowledgeBase(true, selectedStores);
        this.fetchFilesForKnowledgeBase(true, selectedFiles);
      }
    })
  }

  fetchStoresForKnowledgeBase(reset: boolean = false, selectedStores: IKnowledgeBaseStore[] = []) {
    this.Fetching = true;
    this.knowledgeBaseStore.loading = true;
    if (reset) {
      this.knowledgeBaseStore.skip = 0;
      this.knowledgeBaseStore.stores = [];
    }

    this.knowledgeBaseService.getKnowledgeBaseStore({
      search: this.knowledgeBaseStore.searchValue,
      skip: this.knowledgeBaseStore.skip,
      limit: this.knowledgeBaseStore.limit,
      scope: this.scope,
      ...(this.scope == KNOWLEDGE_SCOPE.PROJECT ? { project: this.project as string } : {})
    }).subscribe(res => {
      if (res && res.success) {
        const stores = res.data.stores.map(store => ({ ...store, selected: false }));
        if (reset) {
          this.knowledgeBaseStore.stores = stores;
        } else {
          this.knowledgeBaseStore.stores = [...this.knowledgeBaseStore.stores, ...stores];
        }
        this.knowledgeBaseStore.totalRecords = res.data.total
        this.knowledgeBaseStore.currentPageStores = this.knowledgeBaseStore.stores.slice(this.knowledgeBaseStore.skip, this.knowledgeBaseStore.skip + this.knowledgeBaseStore.limit);
        const existingIds = new Set(this.knowledgeBaseStore.selectedStores.map(store => store._id));
        const newStores = selectedStores.filter(store => !existingIds.has(store._id));
        this.knowledgeBaseStore.selectedStores = [...this.knowledgeBaseStore.selectedStores, ...newStores];
        this.checkSelectedStores();
      }
      this.Fetching = false;
      this.knowledgeBaseStore.loading = false;
    })
  }

  fetchFilesForKnowledgeBase(reset: boolean = false, selectedFiles: IFile[] = []) {
    this.Fetching = true;
    this.knowledgeBaseFiles.loading = true;
    if (reset) {
      this.knowledgeBaseFiles.skip = 0;
      this.knowledgeBaseFiles.files = [];
    }
    this.repositoryService.getAnyDocTypeFiles({
      search: this.knowledgeBaseFiles.searchValue,
      skip: this.knowledgeBaseFiles.skip,
      limit: this.knowledgeBaseFiles.limit,
      reqOnlyFiles: true
    }).subscribe(res => {

      if (res && res.success) {
        const files = res.data.files.map(file => ({ ...file, selected: false }));
        if (reset) {
          this.knowledgeBaseFiles.files = files;
        } else {
          this.knowledgeBaseFiles.files = [...this.knowledgeBaseFiles.files, ...files];
        }
        this.knowledgeBaseFiles.totalRecords = res.data.total
        this.knowledgeBaseFiles.currentPageFiles = this.knowledgeBaseFiles.files.slice(this.knowledgeBaseFiles.skip, this.knowledgeBaseFiles.skip + this.knowledgeBaseFiles.limit);
        const existingIds = new Set(this.knowledgeBaseFiles.selectedFiles.map(file => file._id));
        const newFiles = selectedFiles.filter(file => !existingIds.has(file._id));
        this.knowledgeBaseFiles.selectedFiles = [...this.knowledgeBaseFiles.selectedFiles, ...newFiles];
        this.checkSelectedFiles();
      }
      this.Fetching = false;
      this.knowledgeBaseFiles.loading = false;
    });
  }

  onKnowledgeBaseSearch() {
    this.fetchStoresForKnowledgeBase(true);
  }

  checkSelectedFiles() {
    this.knowledgeBaseFiles.currentPageFiles = this.knowledgeBaseFiles.currentPageFiles.map(file => ({
      ...file,
      selected: this.knowledgeBaseFiles.selectedFiles.some(selected => selected._id === file._id)
    }));
    const allSelected = this.knowledgeBaseFiles.currentPageFiles.length > 0 && this.knowledgeBaseFiles.currentPageFiles.every(file =>
      this.knowledgeBaseFiles.selectedFiles.some(selected => selected._id === file._id)
    );
    this.knowledgeBaseFiles.selectAll = allSelected;
  }

  checkSelectedStores() {
    this.knowledgeBaseStore.currentPageStores = this.knowledgeBaseStore.currentPageStores.map(store => ({
      ...store,
      selected: this.knowledgeBaseStore.selectedStores.some(selected => selected._id === store._id)
    }));
    const allSelected = this.knowledgeBaseStore.currentPageStores.length > 0 && this.knowledgeBaseStore.currentPageStores.every(store =>
      this.knowledgeBaseStore.selectedStores.some(selected => selected._id === store._id)
    );
    this.knowledgeBaseStore.selectAll = allSelected;
  }

  onSelectAllKnowledgeBases(event: any) {
    this.knowledgeBaseStore.selectAll = event.checked;
    this.knowledgeBaseStore.currentPageStores.forEach(store => store.selected = event.checked);
    if (event.checked) {
      const selectedSet = new Set(this.knowledgeBaseStore.selectedStores.map(s => s._id));
      this.knowledgeBaseStore.currentPageStores.forEach(store => {
        if (!selectedSet.has(store._id)) {
          this.knowledgeBaseStore.selectedStores.push(store);
        }
      });
    } else {
      const currentPageIds = this.knowledgeBaseStore.currentPageStores.map(store => store._id);
      this.knowledgeBaseStore.selectedStores = this.knowledgeBaseStore.selectedStores.filter(store => !currentPageIds.includes(store._id));
    }
  }

  onKnowledgeBaseCheckboxChange(store: IKnowledgeBaseStore & { selected: boolean }, checked: boolean) {
    store.selected = checked;
    if (checked) {
      this.knowledgeBaseStore.selectedStores = [...this.knowledgeBaseStore.selectedStores, store];
    } else {
      this.knowledgeBaseStore.selectedStores = this.knowledgeBaseStore.selectedStores.filter(s => s._id !== store._id);
    }
    this.knowledgeBaseStore.currentPageStores = this.knowledgeBaseStore.currentPageStores.map(s => s._id === store._id ? { ...s, selected: checked } : s);
    this.knowledgeBaseStore.selectAll = this.knowledgeBaseStore.currentPageStores.every(store => store.selected);
  }

  onKnowledgeBasePageChange(event: PaginatorState) {
    this.knowledgeBaseStore.selectAll = false;
    this.knowledgeBaseStore.skip = event.first ?? 0;
    this.knowledgeBaseStore.limit = event.rows ?? 10;
    if ((this.knowledgeBaseStore.stores ?? []).length > this.knowledgeBaseStore.skip) {
      this.knowledgeBaseStore.currentPageStores = this.knowledgeBaseStore.stores.slice(this.knowledgeBaseStore.skip, this.knowledgeBaseStore.skip + this.knowledgeBaseStore.limit);
    } else {
      this.fetchStoresForKnowledgeBase();
    }
  }

  onRemoveKnowledgeBase(kb: IKnowledgeBaseStore) {
    this.knowledgeBaseStore.selectedStores = this.knowledgeBaseStore.selectedStores.filter(store => store._id !== kb._id);
    this.knowledgeBaseStore.currentPageStores = this.knowledgeBaseStore.currentPageStores.map(store => store._id === kb._id ? { ...store, selected: false } : store);
    this.knowledgeBaseStore.selectAll = false;
  }

  onKnowledgeBaseFilesSearch() {
    this.knowledgeBaseFiles.skip = 0;
    this.fetchFilesForKnowledgeBase(true);
  }

  onSelectAllKnowledgeBasesFiles(event: any) {
    this.knowledgeBaseFiles.selectAll = event.checked;
    this.knowledgeBaseFiles.currentPageFiles.forEach(file => file.selected = event.checked);

    if (event.checked) {
      const selectedSet = new Set(this.knowledgeBaseFiles.selectedFiles.map(f => f._id));
      this.knowledgeBaseFiles.currentPageFiles.forEach(file => {
        if (!selectedSet.has(file._id)) {
          this.knowledgeBaseFiles.selectedFiles.push(file);
        }
      });
    } else {
      const currentPageIds = this.knowledgeBaseFiles.currentPageFiles.map(file => file._id);
      this.knowledgeBaseFiles.selectedFiles = this.knowledgeBaseFiles.selectedFiles.filter(file => !currentPageIds.includes(file._id));
    }
  }


  onKnowledgeBaseFilesCheckboxChange(file: IFile & { selected: boolean }, checked: boolean) {
    if (checked) {
      const alreadySelected = this.knowledgeBaseFiles.selectedFiles.some(selected => selected._id === file._id);
      if (!alreadySelected) {
        this.knowledgeBaseFiles.selectedFiles.push(file);
      }
    } else {
      this.knowledgeBaseFiles.selectedFiles = this.knowledgeBaseFiles.selectedFiles.filter(f => f._id !== file._id);
    }
    this.knowledgeBaseFiles.currentPageFiles = this.knowledgeBaseFiles.currentPageFiles.map(f =>
      f._id === file._id ? { ...f, selected: checked } : f
    );
    this.knowledgeBaseFiles.selectAll = this.knowledgeBaseFiles.currentPageFiles.every(file => file.selected);
  }


  onKnowledgeBaseFilesPageChange(event: PaginatorState) {
    this.knowledgeBaseFiles.selectAll = false;
    this.knowledgeBaseFiles.skip = event.first ?? 0;
    this.knowledgeBaseFiles.limit = event.rows ?? 10;
    if ((this.knowledgeBaseFiles.files ?? []).length > this.knowledgeBaseFiles.skip) {
      this.knowledgeBaseFiles.currentPageFiles = this.knowledgeBaseFiles.files.slice(this.knowledgeBaseFiles.skip, this.knowledgeBaseFiles.skip + this.knowledgeBaseFiles.limit);
    } else {
      this.fetchFilesForKnowledgeBase();
    }
  }

  onRemoveKnowledgeBaseFile(file: IFile) {
    this.knowledgeBaseFiles.selectedFiles = this.knowledgeBaseFiles.selectedFiles.filter(f => f._id !== file._id);
    this.knowledgeBaseFiles.files = this.knowledgeBaseFiles.files.map(f =>
      f._id === file._id ? { ...f, selected: false } : f
    );
    this.knowledgeBaseFiles.currentPageFiles = this.knowledgeBaseFiles.currentPageFiles.map(f =>
      f._id === file._id ? { ...f, selected: false } : f as IFile & { selected: boolean }
    );
    this.knowledgeBaseFiles.selectAll = this.knowledgeBaseFiles.currentPageFiles.every(file => file.selected);
  }

  onRemoveFromCombinedList(item: { type: 'knowledgeBase' | 'file', item: IKnowledgeBaseStore | IFile }) {
    if (item.type === 'knowledgeBase') {
      this.onRemoveKnowledgeBase(item.item as IKnowledgeBaseStore);
    } else {
      this.onRemoveKnowledgeBaseFile(item.item as IFile);
    }
  }

  getItemDescription(item: { type: 'knowledgeBase' | 'file', item: IKnowledgeBaseStore | IFile }): string {
    if (item.type === 'knowledgeBase') {
      return (item.item as IKnowledgeBaseStore).description;
    }
    return '';
  }

  getItemMimeType(item: { type: 'knowledgeBase' | 'file', item: IKnowledgeBaseStore | IFile }): string {
    if (item.type === 'file') {
      return (item.item as IFile).mimeType || '';
    }
    return '';
  }

  getItemType(item: { type: 'knowledgeBase' | 'file', item: IKnowledgeBaseStore | IFile }): string {
    if (item.type === 'file') {
      return (item.item as IFile).type;
    }
    return '';
  }

  getVerbosityFromLevel(level: number) {
    switch (level) {
      case 0:
        return VERBOSITY.CONCISE;
      case 1:
        return VERBOSITY.MODERATE;
      case 2:
        return VERBOSITY.DETAILED;
      default:
        return VERBOSITY.CONCISE;
    }
  }

  getLevelFromVerbosity(verbosity: VERBOSITY): number {
    switch (verbosity) {
      case VERBOSITY.CONCISE:
        return 0;
      case VERBOSITY.MODERATE:
        return 1;
      case VERBOSITY.DETAILED:
        return 2;
      default:
        return 0;
    }
  }

  setVerbosity(level: number) {
    this.verbosityLevel = level;
    this.verbosity = this.getVerbosityFromLevel(level)
  }

  showUploadDialog = false;

  get uploadDialogData() {
    return {
      origin: 'forFiles',
      type: 'file',
      customTitle: 'Upload File',
      scope: this.scope,
    };
  }

  uploadFile() {
    this.showUploadDialog = true;
  }

  onUploadDialogClosed(res: any) {
    this.showUploadDialog = false;
    if (res) {
      this.fetchFilesForKnowledgeBase(true, res);
    }
  }

  addUserToShare(shareUser: Partial<IShareUser>) {
    if (this.mode == 'create') {
      this.usersToAddToShare.push(shareUser)
    }
  }

  removeUserFromShare(shareUser: Partial<IShareUser>) {
    if (this.mode == 'create') {
      this.usersToAddToShare = this.usersToAddToShare.filter(user => user.email !== shareUser.email)
    }
  }

  updateSharedUser(shareUser: IShareUser) {
    if (this.mode == 'create') {
      this.usersToAddToShare = this.usersToAddToShare.map(user => user.email === shareUser.email ? shareUser : user)
    }
  }

  changeAnyoneAccess(access: SHARE_ACCESS) {
    this.shareService.updateAnyoneAccess((this.document?.contract as IContract)?._id ?? '', access).subscribe(res => {
      if (res.success) {
        this.toastService.success('Access updated successfully')
      }
    })
  }


  onMilestoneSave(contractId: string, event: { edited: Partial<IMilestone>[]; deleted: string[] }) {
    this.milestoneService.updateMilestones(event.edited, event.deleted, MILESTONE_TYPE.DOCUMENT, undefined, contractId).subscribe(res => {
      if (res.success) {
        this.milestones = res.data || [];
        this.editableMilestones = this.milestones.map(m => ({
          ...m,
          date: m.date ? new Date(m.date) : new Date(new Date().setHours(23, 59, 59, 999)),
          reminder: m.reminder
        }));
        this.deletedMilestoneIds = [];
        this.toastService.success('Milestones saved successfully.');
      } else {
        this.toastService.error('Failed to save milestones.');
      }
      this.Saving = false;
    });
  }

  getMilestones() {
    if (!this.document?.contract) {
      return;
    }
    this.milestoneService.getMilestones(MILESTONE_TYPE.DOCUMENT, undefined, (this.document?.contract as IContract)?._id ?? '').subscribe(res => {
      if (res.success) {
        this.milestones = res.data || [];
        this.editableMilestones = this.milestones.map(m => ({
          ...m,
          date: m.date ? new Date(m.date) : new Date(new Date().setHours(23, 59, 59, 999)),
          reminder: m.reminder
        }));
        this.deletedMilestoneIds = [];
      }
    });
  }

  getDocTypeMilestones() {
    if (!this.docType?._id) {
      return;
    }
    this.milestoneService.getMilestones(MILESTONE_TYPE.DOCUMENT, undefined, undefined, this.docType._id).subscribe(res => {
      if (res.success) {
        this.milestones = res.data || [];
        this.editableMilestones = this.milestones.map(m => ({
          ...m,
          date: m.date ? new Date(m.date) : new Date(new Date().setHours(23, 59, 59, 999)),
          reminder: m.reminder
        }));
        this.deletedMilestoneIds = [];
      }
    });
  }

  onMilestonesChange(milestones: Partial<IMilestone>[]) {
    this.milestones = milestones as IMilestone[];
  }

  get uniqueSelectedFiles(): IFile[] {
    return this.knowledgeBaseFiles.selectedFiles;
  }

  get combinedSelectedItems(): Array<{ type: 'knowledgeBase' | 'file', item: IKnowledgeBaseStore | IFile }> {
    const combined: Array<{ type: 'knowledgeBase' | 'file', item: IKnowledgeBaseStore | IFile }> = [];

    this.knowledgeBaseStore.selectedStores.forEach(kb => {
      combined.push({ type: 'knowledgeBase', item: kb });
    });

    this.uniqueSelectedFiles.forEach(file => {
      combined.push({ type: 'file', item: file });
    });

    return combined;
  }

}
